import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Badge,
  Button,
  Form,
  Row,
  Col,
  Modal,
  Spinner,
  Alert,
  InputGroup,
  Tabs,
  Tab,
} from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { adminService } from "../../services/adminService";
import toast from "react-hot-toast";
import ImageUploadField from "../../components/Common/ImageUploadField";
import { IMAGE_SPECS } from "../../utils/imageSpecs";
import "./AdvertiseWithUs.css";

// Quill editor modules configuration
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image", "video"],
    [{ align: [] }],
    [{ color: [] }, { background: [] }],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
  "align",
  "color",
  "background",
];

const PAGE_KEY = "advertise-with-us";

const UPLOAD_FOLDER = "advertise";

interface PageConfig {
  id: string;
  page_key: string;
  title: string | null;
  subtitle: string | null;
  description: string | null;
  meta_title: string | null;
  meta_description: string | null;
  is_active: boolean;
}

interface PageSection {
  id: string;
  page_config_id: string;
  slug: string;
  title: string;
  display_order: number;
  is_active: boolean;
  data: {
    section_type?: string;
    tab_group?: string;
    content?: string;
    content_heading?: string;
    list_heading?: string;
    images?: Array<{ url: string; caption: string }>;
    list_items?: string[];
    cards?: Array<{
      title: string;
      image: string;
      items: string[];
    }>;
    rate_rows?: Array<{
      description: string;
      rate: string;
      special: string;
      gst: string;
      duration: string;
      unit: string;
    }>;
    download_title?: string;
    button_text?: string;
    document_url?: string;
    whatsapp_number?: string;
    whatsapp_prefilled_text?: string;
    // advertising_details section
    heading?: string;
    subtitle?: string;
    background_image?: string;
    qr_image?: string;
    qr_caption?: string;
    pdf_label?: string;
    pdf_title?: string;
    pdf_description?: string;
    button_url?: string;
  };
}

interface TabConfig {
  id: string;
  slug: string;
  title: string;
  display_order: number;
  is_active: boolean;
  tab_group: string;
}

const AdvertiseWithUs: React.FC = () => {
  const [, setConfig] = useState<PageConfig | null>(null);
  const [sections, setSections] = useState<PageSection[]>([]);
  const [tabs, setTabs] = useState<TabConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);

  const [heroTitle, setHeroTitle] = useState("");
  const [heroDescription, setHeroDescription] = useState("");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [isActive, setIsActive] = useState(true);

  const [showSectionModal, setShowSectionModal] = useState(false);
  const [sectionModalMode, setSectionModalMode] = useState<"add" | "edit">(
    "add",
  );
  const [selectedSection, setSelectedSection] = useState<PageSection | null>(
    null,
  );

  const [sectionTitle, setSectionTitle] = useState("");
  const [sectionSlug, setSectionSlug] = useState("");
  const [sectionTabGroup, setSectionTabGroup] = useState("onscreen");
  const [sectionContent, setSectionContent] = useState("");
  const [sectionImages, setSectionImages] = useState<
    Array<{ url: string; caption: string }>
  >([]);
  const [sectionCards, setSectionCards] = useState<
    Array<{ title: string; image: string; items: string[] }>
  >([]);
  const [sectionWhatsappNumber, setSectionWhatsappNumber] = useState("");
  const [sectionWhatsappText, setSectionWhatsappText] = useState("");
  const [sectionDisplayOrder, setSectionDisplayOrder] = useState(1);
  const [sectionIsActive, setSectionIsActive] = useState(true);
  const [savingSection, setSavingSection] = useState(false);

  const [uploadingImageIndex, setUploadingImageIndex] = useState<number | null>(
    null,
  );
  const [uploadingCardIndex, setUploadingCardIndex] = useState<number | null>(
    null,
  );

  const [showRateCardModal, setShowRateCardModal] = useState(false);
  const [rateCardRows, setRateCardRows] = useState<
    Array<{
      description: string;
      rate: string;
      special: string;
      gst: string;
      duration: string;
      unit: string;
    }>
  >([]);
  const [savingRateCard, setSavingRateCard] = useState(false);

  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadTitle, setDownloadTitle] = useState("");
  const [buttonText, setButtonText] = useState("");
  const [documentUrl, setDocumentUrl] = useState("");
  const [savingDownload, setSavingDownload] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsHeading, setDetailsHeading] = useState("");
  const [detailsSubtitle, setDetailsSubtitle] = useState("");
  const [detailsBackgroundImage, setDetailsBackgroundImage] = useState("");
  const [detailsQrImage, setDetailsQrImage] = useState("");
  const [detailsQrCaption, setDetailsQrCaption] = useState("");
  const [detailsPdfLabel, setDetailsPdfLabel] = useState("PDF Document");
  const [detailsPdfTitle, setDetailsPdfTitle] = useState(
    "Detailed Advertising Guide",
  );
  const [detailsPdfDescription, setDetailsPdfDescription] = useState("");
  const [detailsButtonText, setDetailsButtonText] = useState(
    "Download Detailed Document",
  );
  const [detailsButtonUrl, setDetailsButtonUrl] = useState("");
  const [detailsIsActive, setDetailsIsActive] = useState(true);
  const [savingDetails, setSavingDetails] = useState(false);
  const [uploadingDetailsBackground, setUploadingDetailsBackground] =
    useState(false);
  const [uploadingDetailsQr, setUploadingDetailsQr] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<PageSection | null>(
    null,
  );

  // Tab management state
  const [showTabModal, setShowTabModal] = useState(false);
  const [tabModalMode, setTabModalMode] = useState<"add" | "edit">("add");
  const [selectedTab, setSelectedTab] = useState<TabConfig | null>(null);
  const [tabTitle, setTabTitle] = useState("");
  const [tabSlug, setTabSlug] = useState("");
  const [tabDisplayOrder, setTabDisplayOrder] = useState(1);
  const [tabIsActive, setTabIsActive] = useState(true);
  const [savingTab, setSavingTab] = useState(false);

  const [showDeleteTabModal, setShowDeleteTabModal] = useState(false);
  const [tabToDelete, setTabToDelete] = useState<TabConfig | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await adminService.getPageConfigByKey(PAGE_KEY);
      if (response.success && response.data) {
        const data = response.data;
        setConfig(data);
        setSections(data.sections || []);
        setHeroTitle(data.title || "");
        setHeroDescription(data.description || "");
        setMetaTitle(data.meta_title || "");
        setMetaDescription(data.meta_description || "");
        setIsActive(Boolean(data.is_active));

        // Load tabs from sections with section_type "tab"
        const tabSections = (data.sections || []).filter(
          (s: PageSection) => s.data?.section_type === "tab",
        );
        const loadedTabs: TabConfig[] = tabSections.map((s: PageSection) => ({
          id: s.id,
          slug: s.slug,
          title: s.title,
          display_order: s.display_order,
          is_active: s.is_active,
          tab_group: s.data?.tab_group || s.slug,
        }));

        // Tabs are now seeded directly in the DB, so we just read them
        setTabs(loadedTabs);
      }
    } catch (error: any) {
      console.error("Error loading page config:", error);
      toast.error("Failed to load page configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHeroConfig = async () => {
    try {
      setSavingConfig(true);
      const response = await adminService.updatePageConfig(PAGE_KEY, {
        title: heroTitle,
        description: heroDescription,
        metaTitle: metaTitle,
        metaDescription: metaDescription,
        isActive: isActive,
      });
      if (response.success) {
        toast.success("Hero configuration saved successfully");
        setConfig(response.data);
      } else {
        toast.error(response.message || "Failed to save hero configuration");
      }
    } catch (error: any) {
      console.error("Error saving hero config:", error);
      toast.error("Failed to save hero configuration");
    } finally {
      setSavingConfig(false);
    }
  };

  const openAddSectionModal = () => {
    setSectionModalMode("add");
    setSelectedSection(null);
    resetSectionForm();
    setSectionDisplayOrder(sections.length + 1);
    setShowSectionModal(true);
  };

  const openEditSectionModal = (section: PageSection) => {
    setSectionModalMode("edit");
    setSelectedSection(section);
    setSectionTitle(section.title);
    setSectionSlug(section.slug);
    setSectionTabGroup(section.data?.tab_group || "onscreen");
    setSectionContent(section.data?.content || "");
    setSectionImages(section.data?.images || []);
    setSectionCards(section.data?.cards || []);
    setSectionWhatsappNumber(section.data?.whatsapp_number || "");
    setSectionWhatsappText(section.data?.whatsapp_prefilled_text || "");
    setSectionDisplayOrder(section.display_order);
    setSectionIsActive(section.is_active);
    setShowSectionModal(true);
  };

  const resetSectionForm = () => {
    setSectionTitle("");
    setSectionSlug("");
    setSectionTabGroup(tabs.length > 0 ? tabs[0].tab_group : "onscreen");
    setSectionContent("");
    setSectionImages([]);
    setSectionCards([]);
    setSectionWhatsappNumber("");
    setSectionWhatsappText("");
    setSectionIsActive(true);
  };

  const handleSaveSection = async () => {
    if (!sectionTitle.trim()) {
      toast.error("Section title is required");
      return;
    }
    try {
      setSavingSection(true);
      const sectionData = {
        title: sectionTitle,
        slug: sectionSlug || undefined,
        displayOrder: sectionDisplayOrder,
        isActive: sectionIsActive,
        data: {
          section_type: "accordion",
          tab_group: sectionTabGroup,
          content: sectionContent,
          images: sectionImages.filter((img) => img.url.trim()),
          cards: sectionCards.filter(
            (card) => card.title.trim() || card.image.trim(),
          ),
          whatsapp_number: sectionWhatsappNumber,
          whatsapp_prefilled_text: sectionWhatsappText,
        },
      };
      let response;
      if (sectionModalMode === "add") {
        response = await adminService.createPageSection(PAGE_KEY, sectionData);
      } else {
        if (!selectedSection) return;
        response = await adminService.updatePageSection(
          PAGE_KEY,
          selectedSection.id,
          sectionData,
        );
      }
      if (response.success) {
        toast.success(
          sectionModalMode === "add"
            ? "Section created successfully"
            : "Section updated successfully",
        );
        setShowSectionModal(false);
        loadData();
      } else {
        toast.error(response.message || "Failed to save section");
      }
    } catch (error: any) {
      console.error("Error saving section:", error);
      toast.error("Failed to save section");
    } finally {
      setSavingSection(false);
    }
  };

  const handleDeleteSection = async () => {
    if (!sectionToDelete) return;
    try {
      const response = await adminService.deletePageSection(
        PAGE_KEY,
        sectionToDelete.id,
      );
      if (response.success) {
        toast.success("Section deleted successfully");
        setShowDeleteModal(false);
        setSectionToDelete(null);
        loadData();
      } else {
        toast.error(response.message || "Failed to delete section");
      }
    } catch (error: any) {
      console.error("Error deleting section:", error);
      toast.error("Failed to delete section");
    }
  };

  // Tab management functions
  const openAddTabModal = () => {
    setTabModalMode("add");
    setSelectedTab(null);
    setTabTitle("");
    setTabSlug("");
    setTabDisplayOrder(tabs.length + 1);
    setTabIsActive(true);
    setShowTabModal(true);
  };

  const openEditTabModal = (tab: TabConfig) => {
    setTabModalMode("edit");
    setSelectedTab(tab);
    setTabTitle(tab.title);
    setTabSlug(tab.slug);
    setTabDisplayOrder(tab.display_order);
    setTabIsActive(tab.is_active);
    setShowTabModal(true);
  };

  const handleSaveTab = async () => {
    if (!tabTitle.trim()) {
      toast.error("Tab title is required");
      return;
    }
    if (!tabSlug.trim()) {
      toast.error("Tab slug is required");
      return;
    }
    try {
      setSavingTab(true);
      const tabData = {
        title: tabTitle,
        slug: tabSlug,
        displayOrder: tabDisplayOrder,
        isActive: tabIsActive,
        data: {
          section_type: "tab",
          tab_group: tabSlug,
        },
      };
      let response;
      if (tabModalMode === "add") {
        response = await adminService.createPageSection(PAGE_KEY, tabData);
      } else {
        if (!selectedTab) return;
        response = await adminService.updatePageSection(
          PAGE_KEY,
          selectedTab.id,
          tabData,
        );
      }
      if (response.success) {
        toast.success(
          tabModalMode === "add"
            ? "Tab created successfully"
            : "Tab updated successfully",
        );
        setShowTabModal(false);
        loadData();
      } else {
        toast.error(response.message || "Failed to save tab");
      }
    } catch (error: any) {
      console.error("Error saving tab:", error);
      toast.error("Failed to save tab");
    } finally {
      setSavingTab(false);
    }
  };

  const handleDeleteTab = async () => {
    if (!tabToDelete) return;
    try {
      const response = await adminService.deletePageSection(
        PAGE_KEY,
        tabToDelete.id,
      );
      if (response.success) {
        toast.success("Tab deleted successfully");
        setShowDeleteTabModal(false);
        setTabToDelete(null);
        loadData();
      } else {
        toast.error(response.message || "Failed to delete tab");
      }
    } catch (error: any) {
      console.error("Error deleting tab:", error);
      toast.error("Failed to delete tab");
    }
  };

  const handleAddImage = () => {
    setSectionImages([...sectionImages, { url: "", caption: "" }]);
  };
  const handleRemoveImage = (index: number) => {
    setSectionImages(sectionImages.filter((_, i) => i !== index));
  };
  const handleImageChange = (
    index: number,
    field: "url" | "caption",
    value: string,
  ) => {
    const updated = [...sectionImages];
    updated[index] = { ...updated[index], [field]: value };
    setSectionImages(updated);
  };

  const uploadSectionImage = async (
    file: File,
    index: number,
  ): Promise<string | null> => {
    setUploadingImageIndex(index);
    try {
      const response = await adminService.uploadPageAsset(file, UPLOAD_FOLDER);
      if (response.success && response.data) {
        const url = response.data.url || response.data.filename;
        handleImageChange(index, "url", url);
        toast.success("Image uploaded successfully");
        return url;
      }
      toast.error("Upload failed");
      return null;
    } catch (error: any) {
      console.error("Error uploading image:", error);
      toast.error("Failed to upload image");
      return null;
    } finally {
      setUploadingImageIndex(null);
    }
  };

  const handleAddCard = () => {
    setSectionCards([...sectionCards, { title: "", image: "", items: [] }]);
  };
  const handleRemoveCard = (index: number) => {
    setSectionCards(sectionCards.filter((_, i) => i !== index));
  };
  const handleCardChange = (
    index: number,
    field: "title" | "image",
    value: string,
  ) => {
    const updated = [...sectionCards];
    updated[index] = { ...updated[index], [field]: value };
    setSectionCards(updated);
  };
  const handleAddCardItem = (cardIndex: number) => {
    const updated = [...sectionCards];
    updated[cardIndex] = {
      ...updated[cardIndex],
      items: [...updated[cardIndex].items, ""],
    };
    setSectionCards(updated);
  };
  const handleRemoveCardItem = (cardIndex: number, itemIndex: number) => {
    const updated = [...sectionCards];
    updated[cardIndex] = {
      ...updated[cardIndex],
      items: updated[cardIndex].items.filter((_, i) => i !== itemIndex),
    };
    setSectionCards(updated);
  };
  const handleCardItemChange = (
    cardIndex: number,
    itemIndex: number,
    value: string,
  ) => {
    const updated = [...sectionCards];
    updated[cardIndex] = {
      ...updated[cardIndex],
      items: updated[cardIndex].items.map((item, i) =>
        i === itemIndex ? value : item,
      ),
    };
    setSectionCards(updated);
  };

  const uploadCardImage = async (
    file: File,
    cardIndex: number,
  ): Promise<string | null> => {
    setUploadingCardIndex(cardIndex);
    try {
      const response = await adminService.uploadPageAsset(file, UPLOAD_FOLDER);
      if (response.success && response.data) {
        const url = response.data.url || response.data.filename;
        handleCardChange(cardIndex, "image", url);
        toast.success("Card image uploaded successfully");
        return url;
      }
      toast.error("Upload failed");
      return null;
    } catch (error: any) {
      console.error("Error uploading card image:", error);
      toast.error("Failed to upload image");
      return null;
    } finally {
      setUploadingCardIndex(null);
    }
  };

  const openRateCardModal = () => {
    const rateCardSection = sections.find(
      (s) => s.data?.section_type === "rate_card",
    );
    setRateCardRows(rateCardSection?.data?.rate_rows || []);
    setShowRateCardModal(true);
  };

  const handleAddRateRow = () => {
    setRateCardRows([
      ...rateCardRows,
      {
        description: "",
        rate: "",
        special: "",
        gst: "18%",
        duration: "",
        unit: "",
      },
    ]);
  };
  const handleRemoveRateRow = (index: number) =>
    setRateCardRows(rateCardRows.filter((_, i) => i !== index));
  const handleRateRowChange = (
    index: number,
    field: keyof (typeof rateCardRows)[0],
    value: string,
  ) => {
    const updated = [...rateCardRows];
    updated[index] = { ...updated[index], [field]: value };
    setRateCardRows(updated);
  };

  const handleSaveRateCard = async () => {
    try {
      setSavingRateCard(true);
      const rateCardSection = sections.find(
        (s) => s.data?.section_type === "rate_card",
      );
      const sectionData = {
        title: "Rate Card",
        slug: "rate-card",
        displayOrder: 999,
        isActive: true,
        data: {
          section_type: "rate_card",
          rate_rows: rateCardRows.filter((row) => row.description.trim()),
        },
      };
      let response;
      if (rateCardSection) {
        response = await adminService.updatePageSection(
          PAGE_KEY,
          rateCardSection.id,
          sectionData,
        );
      } else {
        response = await adminService.createPageSection(PAGE_KEY, sectionData);
      }
      if (response.success) {
        toast.success("Rate card saved successfully");
        setShowRateCardModal(false);
        loadData();
      } else {
        toast.error(response.message || "Failed to save rate card");
      }
    } catch (error: any) {
      console.error("Error saving rate card:", error);
      toast.error("Failed to save rate card");
    } finally {
      setSavingRateCard(false);
    }
  };

  const openDownloadModal = () => {
    const downloadSection = sections.find(
      (s) => s.data?.section_type === "download",
    );
    setDownloadTitle(downloadSection?.data?.download_title || "");
    setButtonText(downloadSection?.data?.button_text || "Download");
    setDocumentUrl(downloadSection?.data?.document_url || "");
    setShowDownloadModal(true);
  };

  const uploadDocument = async (file: File): Promise<string | null> => {
    setUploadingDocument(true);
    try {
      const response = await adminService.uploadDocument(file);
      if (response.success && response.data) {
        const url = response.data.url || response.data.filename;
        setDocumentUrl(url);
        toast.success("Document uploaded successfully");
        return url;
      }
      toast.error("Upload failed");
      return null;
    } catch (error: any) {
      console.error("Error uploading document:", error);
      toast.error("Failed to upload document");
      return null;
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleSaveDownload = async () => {
    try {
      setSavingDownload(true);
      const downloadSection = sections.find(
        (s) => s.data?.section_type === "download",
      );
      const sectionData = {
        title: "Download Section",
        slug: "download-section",
        displayOrder: 1000,
        isActive: true,
        data: {
          section_type: "download",
          download_title: downloadTitle,
          button_text: buttonText,
          document_url: documentUrl,
        },
      };
      let response;
      if (downloadSection) {
        response = await adminService.updatePageSection(
          PAGE_KEY,
          downloadSection.id,
          sectionData,
        );
      } else {
        response = await adminService.createPageSection(PAGE_KEY, sectionData);
      }
      if (response.success) {
        toast.success("Download section saved successfully");
        setShowDownloadModal(false);
        loadData();
      } else {
        toast.error(response.message || "Failed to save download section");
      }
    } catch (error: any) {
      console.error("Error saving download section:", error);
      toast.error("Failed to save download section");
    } finally {
      setSavingDownload(false);
    }
  };

  // Filter sections by tab using tab_group (not slug)
  const getSectionsForTab = (tabGroup: string) => {
    return sections.filter(
      (s) =>
        s.data?.tab_group === tabGroup &&
        s.data?.section_type !== "rate_card" &&
        s.data?.section_type !== "download" &&
        s.data?.section_type !== "advertising_details" &&
        s.data?.section_type !== "tab" &&
        s.data?.section_type !== "ui_labels",
    );
  };

  const openDetailsModal = () => {
    const detailsSection = sections.find(
      (s) => s.data?.section_type === "advertising_details",
    );
    const d = detailsSection?.data || {};
    setDetailsHeading(d.heading || "Get Our Advertising Details");
    setDetailsSubtitle(d.subtitle || "");
    setDetailsBackgroundImage(d.background_image || "");
    setDetailsQrImage(d.qr_image || "");
    setDetailsQrCaption(d.qr_caption || "");
    setDetailsPdfLabel(d.pdf_label || "PDF Document");
    setDetailsPdfTitle(d.pdf_title || "Detailed Advertising Guide");
    setDetailsPdfDescription(d.pdf_description || "");
    setDetailsButtonText(d.button_text || "Download Detailed Document");
    setDetailsButtonUrl(d.button_url || "");
    setDetailsIsActive(detailsSection?.is_active ?? true);
    setShowDetailsModal(true);
  };

  // Strict dimension check: the file must be exactly the expected width/height.
  // Resolves with `true` if the file matches, `false` otherwise. Side effect:
  // emits a toast error explaining the mismatch.
  const uploadDetailsImage = async (
    file: File,
    target: "background" | "qr",
  ): Promise<string | null> => {
    const setUploading =
      target === "background"
        ? setUploadingDetailsBackground
        : setUploadingDetailsQr;
    const setValue =
      target === "background" ? setDetailsBackgroundImage : setDetailsQrImage;
    const spec =
      target === "background"
        ? IMAGE_SPECS.advertiseDetailsBackground
        : IMAGE_SPECS.advertiseDetailsQr;
    setUploading(true);
    try {
      const response = await adminService.uploadPageAsset(file, UPLOAD_FOLDER);
      if (response.success && response.data) {
        const url = response.data.url || response.data.filename;
        setValue(url);
        toast.success(`${spec.label} uploaded successfully`);
        return url;
      }
      toast.error("Upload failed");
      return null;
    } catch (error: any) {
      console.error("Error uploading details image:", error);
      toast.error("Failed to upload image");
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSaveDetails = async () => {
    try {
      setSavingDetails(true);
      const detailsSection = sections.find(
        (s) => s.data?.section_type === "advertising_details",
      );
      const sectionData = {
        title: detailsHeading || "Get Our Advertising Details",
        slug: "advertising-details",
        displayOrder: 1005,
        isActive: detailsIsActive,
        data: {
          section_type: "advertising_details",
          heading: detailsHeading,
          subtitle: detailsSubtitle,
          background_image: detailsBackgroundImage,
          qr_image: detailsQrImage,
          qr_caption: detailsQrCaption,
          pdf_label: detailsPdfLabel,
          pdf_title: detailsPdfTitle,
          pdf_description: detailsPdfDescription,
          button_text: detailsButtonText,
          button_url: detailsButtonUrl,
        },
      };
      let response;
      if (detailsSection) {
        response = await adminService.updatePageSection(
          PAGE_KEY,
          detailsSection.id,
          sectionData,
        );
      } else {
        response = await adminService.createPageSection(PAGE_KEY, sectionData);
      }
      if (response.success) {
        toast.success("Advertising details saved successfully");
        setShowDetailsModal(false);
        loadData();
      } else {
        toast.error(response.message || "Failed to save advertising details");
      }
    } catch (error: any) {
      console.error("Error saving advertising details:", error);
      toast.error("Failed to save advertising details");
    } finally {
      setSavingDetails(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Advertise With Us - Admin</title>
      </Helmet>

      <div className="container-fluid p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">Advertise With Us</h2>
            <p className="text-muted mb-0">
              Manage content, images, and sections for the Advertise With Us
              page
            </p>
          </div>
        </div>

        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              <i className="bi bi-house-door me-2"></i>
              Hero Section Configuration
            </h5>
          </Card.Header>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label>Hero Title</Form.Label>
              <Form.Control
                type="text"
                value={heroTitle}
                onChange={(e) => setHeroTitle(e.target.value)}
                placeholder="Advertise With Us"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Hero Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={heroDescription}
                onChange={(e) => setHeroDescription(e.target.value)}
                placeholder="Reach thousands of movie lovers..."
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Meta Title (SEO)</Form.Label>
                  <Form.Control
                    type="text"
                    value={metaTitle}
                    onChange={(e) => setMetaTitle(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Meta Description (SEO)</Form.Label>
                  <Form.Control
                    type="text"
                    value={metaDescription}
                    onChange={(e) => setMetaDescription(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="is-active-switch"
                label="Page is active (visible on website)"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            </Form.Group>
            <Button
              variant="primary"
              onClick={handleSaveHeroConfig}
              disabled={savingConfig}
            >
              {savingConfig ? "Saving..." : "Save Hero Configuration"}
            </Button>
          </Card.Body>
        </Card>

        {/* Tabs Management */}
        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="bi bi-tabs me-2"></i>
              Tabs Management
            </h5>
            <Button variant="success" onClick={openAddTabModal}>
              <i className="bi bi-plus-circle me-2"></i>
              Add Tab
            </Button>
          </Card.Header>
          <Card.Body>
            {tabs.length === 0 ? (
              <Alert variant="info">No tabs configured yet.</Alert>
            ) : (
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Tab Name</th>
                    <th>Slug</th>
                    <th>Display Order</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tabs.map((tab, index) => (
                    <tr key={tab.id}>
                      <td>{index + 1}</td>
                      <td>
                        <strong>{tab.title}</strong>
                      </td>
                      <td>
                        <code>{tab.slug}</code>
                      </td>
                      <td>{tab.display_order}</td>
                      <td>
                        {tab.is_active ? (
                          <Badge bg="success">Active</Badge>
                        ) : (
                          <Badge bg="secondary">Inactive</Badge>
                        )}
                      </td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() => openEditTabModal(tab)}
                        >
                          <i className="bi bi-pencil"></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            setTabToDelete(tab);
                            setShowDeleteTabModal(true);
                          }}
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        {/* Content Sections - organized by tabs */}
        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="bi bi-list-ul me-2"></i>
              Content Sections
            </h5>
            <Button variant="success" onClick={openAddSectionModal}>
              <i className="bi bi-plus-circle me-2"></i>
              Add Section
            </Button>
          </Card.Header>
          <Card.Body>
            {tabs.length === 0 ? (
              <Alert variant="warning">
                Please add at least one tab before adding sections.
              </Alert>
            ) : (
              <Tabs defaultActiveKey={tabs[0]?.slug} className="mb-3 cms-tabs">
                {tabs.map((tab) => {
                  const tabSections = getSectionsForTab(tab.tab_group);
                  return (
                    <Tab
                      key={tab.id}
                      eventKey={tab.slug}
                      title={`${tab.title} (${tabSections.length})`}
                    >
                      {tabSections.length === 0 ? (
                        <Alert variant="info">
                          No sections in this tab yet.
                        </Alert>
                      ) : (
                        <Table responsive striped hover>
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Title</th>
                              <th>Slug</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tabSections.map((section, index) => (
                              <tr key={section.id}>
                                <td>{index + 1}</td>
                                <td>
                                  <strong>{section.title}</strong>
                                </td>
                                <td>
                                  <code>{section.slug}</code>
                                </td>
                                <td>
                                  {section.is_active ? (
                                    <Badge bg="success">Active</Badge>
                                  ) : (
                                    <Badge bg="secondary">Inactive</Badge>
                                  )}
                                </td>
                                <td>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="me-2"
                                    onClick={() =>
                                      openEditSectionModal(section)
                                    }
                                  >
                                    <i className="bi bi-pencil"></i>
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => {
                                      setSectionToDelete(section);
                                      setShowDeleteModal(true);
                                    }}
                                  >
                                    <i className="bi bi-trash"></i>
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </Tab>
                  );
                })}
              </Tabs>
            )}
          </Card.Body>
        </Card>

        {/* <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="bi bi-table me-2"></i>
              Rate Card
            </h5>
            <Button variant="primary" onClick={openRateCardModal}>
              Edit Rate Card
            </Button>
          </Card.Header>
          <Card.Body>
            {(() => {
              const rateCardSection = sections.find(
                (s) => s.data?.section_type === "rate_card",
              );
              const rows = rateCardSection?.data?.rate_rows || [];
              if (rows.length === 0) {
                return <Alert variant="info">No rate card rows yet.</Alert>;
              }
              return (
                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Rates</th>
                      <th>Special</th>
                      <th>GST</th>
                      <th>Duration</th>
                      <th>Unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => (
                      <tr key={idx}>
                        <td>{row.description}</td>
                        <td>{row.rate}</td>
                        <td>{row.special}</td>
                        <td>{row.gst}</td>
                        <td>{row.duration}</td>
                        <td>{row.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              );
            })()}
          </Card.Body>
        </Card> */}

        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="bi bi-download me-2"></i>
              Download Section
            </h5>
            <Button variant="primary" onClick={openDownloadModal}>
              Edit Download Section
            </Button>
          </Card.Header>
          <Card.Body>
            {(() => {
              const downloadSection = sections.find(
                (s) => s.data?.section_type === "download",
              );
              if (!downloadSection) {
                return (
                  <Alert variant="info">
                    No download section configured yet.
                  </Alert>
                );
              }
              return (
                <div>
                  <p>
                    <strong>Title:</strong>{" "}
                    {downloadSection.data?.download_title}
                  </p>
                  <p>
                    <strong>Button Text:</strong>{" "}
                    {downloadSection.data?.button_text}
                  </p>
                  <p>
                    <strong>Document URL:</strong>{" "}
                    <code>{downloadSection.data?.document_url}</code>
                  </p>
                </div>
              );
            })()}
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="bi bi-qr-code me-2"></i>
              Get Our Advertising Details
            </h5>
            <Button variant="primary" onClick={openDetailsModal}>
              Edit Advertising Details
            </Button>
          </Card.Header>
          <Card.Body>
            {(() => {
              const detailsSection = sections.find(
                (s) => s.data?.section_type === "advertising_details",
              );
              if (!detailsSection) {
                return (
                  <Alert variant="info">
                    No advertising details section configured yet.
                  </Alert>
                );
              }
              const d = detailsSection.data || {};
              return (
                <div>
                  <p>
                    <strong>Heading:</strong>{" "}
                    {d.heading || "Get Our Advertising Details"}
                  </p>
                  <p>
                    <strong>Subtitle:</strong>{" "}
                    {d.subtitle || (
                      <span className="text-muted">(not set)</span>
                    )}
                  </p>
                  <p>
                    <strong>Button Text:</strong>{" "}
                    {d.button_text || "Download Detailed Document"}
                  </p>
                  <p>
                    <strong>Button URL:</strong>{" "}
                    <code>{d.button_url || "(not set)"}</code>
                  </p>
                  <p>
                    <strong>Status:</strong>{" "}
                    {detailsSection.is_active ? (
                      <Badge bg="success">Active</Badge>
                    ) : (
                      <Badge bg="secondary">Inactive</Badge>
                    )}
                  </p>
                </div>
              );
            })()}
          </Card.Body>
        </Card>
      </div>

      {/* Tab Modal */}
      <Modal
        show={showTabModal}
        onHide={() => setShowTabModal(false)}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {tabModalMode === "add" ? "Add Tab" : "Edit Tab"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Tab Name *</Form.Label>
            <Form.Control
              type="text"
              value={tabTitle}
              onChange={(e) => setTabTitle(e.target.value)}
              placeholder="e.g., On-Screen Advertisements"
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Tab Slug *</Form.Label>
            <Form.Control
              type="text"
              value={tabSlug}
              onChange={(e) => setTabSlug(e.target.value)}
              placeholder="e.g., onscreen"
              required
            />
            <Form.Text className="text-muted">
              Used internally to identify the tab. Use lowercase, no spaces.
            </Form.Text>
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Display Order</Form.Label>
                <Form.Control
                  type="number"
                  value={tabDisplayOrder}
                  onChange={(e) =>
                    setTabDisplayOrder(parseInt(e.target.value) || 1)
                  }
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  label="Active"
                  checked={tabIsActive}
                  onChange={(e) => setTabIsActive(e.target.checked)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTabModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveTab}
            disabled={savingTab}
          >
            {savingTab ? "Saving..." : "Save Tab"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Tab Modal */}
      <Modal
        show={showDeleteTabModal}
        onHide={() => setShowDeleteTabModal(false)}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete Tab</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the tab{" "}
          <strong>{tabToDelete?.title}</strong>?
          <Alert variant="warning" className="mt-3">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Note: Sections in this tab will not be deleted, but they will need
            to be reassigned to another tab.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteTabModal(false)}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteTab}>
            Delete Tab
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Section Modal */}
      <Modal
        show={showSectionModal}
        onHide={() => setShowSectionModal(false)}
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {sectionModalMode === "add" ? "Add Section" : "Edit Section"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Title *</Form.Label>
                <Form.Control
                  type="text"
                  value={sectionTitle}
                  onChange={(e) => setSectionTitle(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Tab Group</Form.Label>
                <Form.Select
                  value={sectionTabGroup}
                  onChange={(e) => setSectionTabGroup(e.target.value)}
                >
                  {tabs.map((tab) => (
                    <option key={tab.id} value={tab.tab_group}>
                      {tab.title}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Slug</Form.Label>
            <Form.Control
              type="text"
              value={sectionSlug}
              onChange={(e) => setSectionSlug(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Content</Form.Label>
            <div style={{ height: "300px", marginBottom: "16px" }}>
              <ReactQuill
                key={
                  showSectionModal
                    ? `quill-${sectionModalMode}-${selectedSection?.id ?? "new"}`
                    : "quill-hidden"
                }
                theme="snow"
                value={sectionContent}
                onChange={(value) => setSectionContent(value)}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Enter section content with formatting (headings, lists, etc.)..."
              />
            </div>
            <Form.Text className="text-muted">
              Use the rich text editor to format content with headings, lists,
              bold, italic, etc.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Images (with captions)</Form.Label>
            {sectionImages.map((img, index) => (
              <div key={index} className="mb-3 p-2 border rounded">
                <Row>
                  <Col md={8}>
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Image URL</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="text"
                          value={img.url}
                          onChange={(e) =>
                            handleImageChange(index, "url", e.target.value)
                          }
                          placeholder="Image URL or upload below"
                        />
                        <ImageUploadField
                          spec={IMAGE_SPECS.advertiseTabImage}
                          onUpload={(file) => uploadSectionImage(file, index)}
                          onChange={(url) =>
                            handleImageChange(index, "url", url)
                          }
                          disabled={uploadingImageIndex === index}
                          buttonVariant="outline-primary"
                          buttonLabel={uploadingImageIndex === index ? "Uploading..." : "Upload"}
                          showPreview={false}
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Caption</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="text"
                          value={img.caption}
                          onChange={(e) =>
                            handleImageChange(index, "caption", e.target.value)
                          }
                          placeholder="e.g., Onscreen - Static"
                        />
                        <Button
                          variant="outline-danger"
                          onClick={() => handleRemoveImage(index)}
                        >
                          <i className="bi bi-x"></i>
                        </Button>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>
                {img.url && (
                  <div className="image-upload-preview">
                    <img src={img.url} alt={`Preview ${index + 1}`} />
                  </div>
                )}
              </div>
            ))}
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleAddImage}
            >
              <i className="bi bi-plus-circle me-1"></i>
              Add Image
            </Button>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Cards</Form.Label>
            {sectionCards.map((card, cardIndex) => (
              <Card key={cardIndex} className="mb-3">
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label>Card Title</Form.Label>
                        <Form.Control
                          type="text"
                          value={card.title}
                          onChange={(e) =>
                            handleCardChange(cardIndex, "title", e.target.value)
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label>Card Image</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            value={card.image}
                            onChange={(e) =>
                              handleCardChange(
                                cardIndex,
                                "image",
                                e.target.value,
                              )
                            }
                            placeholder="Image URL or upload"
                          />
                          <ImageUploadField
                            spec={IMAGE_SPECS.advertiseOffscreenCard}
                            onUpload={(file) => uploadCardImage(file, cardIndex)}
                            onChange={(url) =>
                              handleCardChange(cardIndex, "image", url)
                            }
                            disabled={uploadingCardIndex === cardIndex}
                            buttonVariant="outline-primary"
                            buttonLabel={uploadingCardIndex === cardIndex ? "Uploading..." : "Upload"}
                            showPreview={false}
                          />
                          <Button
                            variant="outline-danger"
                            onClick={() => handleRemoveCard(cardIndex)}
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </InputGroup>
                        {card.image && (
                          <div className="image-upload-preview">
                            <img
                              src={card.image}
                              alt={`Card ${cardIndex + 1}`}
                            />
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Label>Card Items</Form.Label>
                  {card.items.map((item, itemIndex) => (
                    <InputGroup className="mb-2" key={itemIndex}>
                      <Form.Control
                        type="text"
                        value={item}
                        onChange={(e) =>
                          handleCardItemChange(
                            cardIndex,
                            itemIndex,
                            e.target.value,
                          )
                        }
                      />
                      <Button
                        variant="outline-danger"
                        onClick={() =>
                          handleRemoveCardItem(cardIndex, itemIndex)
                        }
                      >
                        <i className="bi bi-x"></i>
                      </Button>
                    </InputGroup>
                  ))}
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleAddCardItem(cardIndex)}
                  >
                    <i className="bi bi-plus-circle me-1"></i>
                    Add Item
                  </Button>
                </Card.Body>
              </Card>
            ))}
            <Button variant="outline-primary" size="sm" onClick={handleAddCard}>
              <i className="bi bi-plus-circle me-1"></i>
              Add Card
            </Button>
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Display Order</Form.Label>
                <Form.Control
                  type="number"
                  value={sectionDisplayOrder}
                  onChange={(e) =>
                    setSectionDisplayOrder(parseInt(e.target.value) || 1)
                  }
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  label="Active"
                  checked={sectionIsActive}
                  onChange={(e) => setSectionIsActive(e.target.checked)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Card className="mb-3">
            <Card.Header>
              <h6 className="mb-0">
                <i className="bi bi-whatsapp me-2"></i>
                WhatsApp Contact (for this section)
              </h6>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>WhatsApp Number</Form.Label>
                <Form.Control
                  type="text"
                  value={sectionWhatsappNumber}
                  onChange={(e) => setSectionWhatsappNumber(e.target.value)}
                  placeholder="e.g., 918086876511"
                />
                <Form.Text className="text-muted">
                  Enter the WhatsApp number with country code (no + or spaces).
                  Leave empty to use default.
                </Form.Text>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Prefilled Message</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={sectionWhatsappText}
                  onChange={(e) => setSectionWhatsappText(e.target.value)}
                  placeholder="e.g., Hi, I'm interested in advertising with My Cinemas"
                />
                <Form.Text className="text-muted">
                  This message will be pre-filled when users click the Contact
                  button.
                </Form.Text>
              </Form.Group>
            </Card.Body>
          </Card>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowSectionModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveSection}
            disabled={savingSection}
          >
            {savingSection ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showRateCardModal}
        onHide={() => setShowRateCardModal(false)}
        size="xl"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Rate Card</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {rateCardRows.map((row, index) => (
            <Card key={index} className="mb-3">
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        type="text"
                        value={row.description}
                        onChange={(e) =>
                          handleRateRowChange(
                            index,
                            "description",
                            e.target.value,
                          )
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>Rates</Form.Label>
                      <Form.Control
                        type="text"
                        value={row.rate}
                        onChange={(e) =>
                          handleRateRowChange(index, "rate", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-2">
                      <Form.Label>Special</Form.Label>
                      <Form.Control
                        type="text"
                        value={row.special}
                        onChange={(e) =>
                          handleRateRowChange(index, "special", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-2">
                      <Form.Label>GST</Form.Label>
                      <Form.Control
                        type="text"
                        value={row.gst}
                        onChange={(e) =>
                          handleRateRowChange(index, "gst", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-2">
                      <Form.Label>Duration</Form.Label>
                      <Form.Control
                        type="text"
                        value={row.duration}
                        onChange={(e) =>
                          handleRateRowChange(index, "duration", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-2">
                      <Form.Label>Unit</Form.Label>
                      <Form.Control
                        type="text"
                        value={row.unit}
                        onChange={(e) =>
                          handleRateRowChange(index, "unit", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleRemoveRateRow(index)}
                >
                  Remove Row
                </Button>
              </Card.Body>
            </Card>
          ))}
          <Button variant="outline-primary" onClick={handleAddRateRow}>
            Add Rate Row
          </Button>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowRateCardModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveRateCard}
            disabled={savingRateCard}
          >
            {savingRateCard ? "Saving..." : "Save Rate Card"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDownloadModal}
        onHide={() => setShowDownloadModal(false)}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Download Section</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              value={downloadTitle}
              onChange={(e) => setDownloadTitle(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Button Text</Form.Label>
            <Form.Control
              type="text"
              value={buttonText}
              onChange={(e) => setButtonText(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Document (PDF)</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                value={documentUrl}
                onChange={(e) => setDocumentUrl(e.target.value)}
                placeholder="Document URL or upload below"
              />
              <ImageUploadField
                onUpload={uploadDocument}
                onChange={(url) => setDocumentUrl(url)}
                disabled={uploadingDocument}
                accept=".pdf,.doc,.docx"
                buttonVariant="outline-primary"
                buttonLabel={uploadingDocument ? "Uploading..." : "Upload"}
                hint="Supported formats: PDF, DOC, DOCX"
                showPreview={false}
              />
            </InputGroup>
            {documentUrl && (
              <div className="mt-2">
                <small className="text-muted">
                  Current document:{" "}
                  <a
                    href={documentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View
                  </a>
                </small>
              </div>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDownloadModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveDownload}
            disabled={savingDownload}
          >
            {savingDownload ? "Saving..." : "Save Download Section"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the section{" "}
          <strong>{sectionToDelete?.title}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteSection}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Advertising Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Heading</Form.Label>
                <Form.Control
                  type="text"
                  value={detailsHeading}
                  onChange={(e) => setDetailsHeading(e.target.value)}
                  placeholder="Get Our Advertising Details"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Button Text</Form.Label>
                <Form.Control
                  type="text"
                  value={detailsButtonText}
                  onChange={(e) => setDetailsButtonText(e.target.value)}
                  placeholder="Download Detailed Document"
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Subtitle</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={detailsSubtitle}
              onChange={(e) => setDetailsSubtitle(e.target.value)}
              placeholder="Explore our complete advertising opportunities..."
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Button URL</Form.Label>
            <Form.Control
              type="text"
              value={detailsButtonUrl}
              onChange={(e) => setDetailsButtonUrl(e.target.value)}
              placeholder="https://drive.google.com/file/d/.../preview"
            />
            <Form.Text className="text-muted">
              The button will open this URL in a new tab. Use a Google Drive
              <code>/preview</code> link for inline preview.
            </Form.Text>
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Background Image</Form.Label>
                <Form.Text className="text-muted d-block mb-2">
                  {IMAGE_SPECS.advertiseDetailsBackground.hint}
                </Form.Text>
                <InputGroup>
                  <Form.Control
                    type="text"
                    value={detailsBackgroundImage}
                    onChange={(e) =>
                      setDetailsBackgroundImage(e.target.value)
                    }
                    placeholder="Background image URL or upload below"
                  />
                  <ImageUploadField
                    spec={IMAGE_SPECS.advertiseDetailsBackground}
                    onUpload={(file) => uploadDetailsImage(file, "background")}
                    onChange={(url) => setDetailsBackgroundImage(url)}
                    disabled={uploadingDetailsBackground}
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    buttonVariant="outline-primary"
                    buttonLabel={uploadingDetailsBackground ? "Uploading..." : "Upload"}
                    showPreview={false}
                  />
                </InputGroup>
                {detailsBackgroundImage && (
                  <div className="image-upload-preview">
                    <img src={detailsBackgroundImage} alt="Background preview" />
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>QR Image</Form.Label>
                <Form.Text className="text-muted d-block mb-2">
                  {IMAGE_SPECS.advertiseDetailsQr.hint}
                </Form.Text>
                <InputGroup>
                  <Form.Control
                    type="text"
                    value={detailsQrImage}
                    onChange={(e) => setDetailsQrImage(e.target.value)}
                    placeholder="QR image URL or upload below"
                  />
                  <ImageUploadField
                    spec={IMAGE_SPECS.advertiseDetailsQr}
                    onUpload={(file) => uploadDetailsImage(file, "qr")}
                    onChange={(url) => setDetailsQrImage(url)}
                    disabled={uploadingDetailsQr}
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    buttonVariant="outline-primary"
                    buttonLabel={uploadingDetailsQr ? "Uploading..." : "Upload"}
                    showPreview={false}
                  />
                </InputGroup>
                {detailsQrImage && (
                  <div className="image-upload-preview">
                    <img src={detailsQrImage} alt="QR preview" />
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>QR Caption</Form.Label>
            <Form.Control
              type="text"
              value={detailsQrCaption}
              onChange={(e) => setDetailsQrCaption(e.target.value)}
              placeholder="Scan to view the detailed advertising document"
            />
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>PDF Label</Form.Label>
                <Form.Control
                  type="text"
                  value={detailsPdfLabel}
                  onChange={(e) => setDetailsPdfLabel(e.target.value)}
                  placeholder="PDF Document"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>PDF Title</Form.Label>
                <Form.Control
                  type="text"
                  value={detailsPdfTitle}
                  onChange={(e) => setDetailsPdfTitle(e.target.value)}
                  placeholder="Detailed Advertising Guide"
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>PDF Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={detailsPdfDescription}
              onChange={(e) => setDetailsPdfDescription(e.target.value)}
              placeholder="All formats, specifications, placement options..."
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              label="Section is active (visible on website)"
              checked={detailsIsActive}
              onChange={(e) => setDetailsIsActive(e.target.checked)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDetailsModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveDetails}
            disabled={savingDetails}
          >
            {savingDetails ? "Saving..." : "Save Advertising Details"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AdvertiseWithUs;
